import {
  PaneResponsiveCardGrid,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  INVITED_PIPELINE_STAGE,
  UNASSIGNED_PIPELINE_COLUMN_ID,
  UNASSIGNED_PIPELINE_STAGE,
  boardPipelineStages,
  groupPipelineApplicantsByStage,
  isInvitedPipelineApplicant,
  partitionPipelineApplicants,
} from '@yeshuman/sdk'
import { useMemo } from 'react'
import { ApplicantCard, type ApplicantAnalysisRetry, type ApplicantScoresGate } from './applicant-card'
import { StageColumnHeader } from './stage-column-header'
import type { ApplicantCard as ApplicantCardModel, Stage } from '../../hooks/use-opportunity-applicants'

interface ListProps {
  stages: Stage[]
  applicants: ApplicantCardModel[]
  onMoveStage: (applicationId: string, stageSlug: string) => void
  onOpenApplicant: (applicationId: string) => void
  onRecordDecision: (applicationId: string, decision: 'hired' | 'rejected' | 'offer') => void
  onOpenThread?: (applicationId: string) => void
  rankByApplicationId: Map<string, number>
  scoresGate?: ApplicantScoresGate
  analysisRetry?: ApplicantAnalysisRetry
  selectedIds: Set<string>
  onToggleSelect: (applicationId: string) => void
  onSelectAll: () => void
  onClearSelection: () => void
}

const INVITED_TOOLTIP = "Invited candidates can't be moved until they apply"

export function ApplicantsBoardList({
  stages,
  applicants,
  onMoveStage,
  onOpenApplicant,
  onRecordDecision,
  onOpenThread,
  rankByApplicationId,
  scoresGate,
  analysisRetry,
  selectedIds,
  onToggleSelect,
  onSelectAll,
  onClearSelection,
}: ListProps) {
  const { invited, pipeline } = useMemo(() => partitionPipelineApplicants(applicants), [applicants])
  const byStage = useMemo(() => groupPipelineApplicantsByStage(pipeline, stages), [pipeline, stages])

  const selectableApplicants = useMemo(
    () => applicants.filter((applicant) => !isInvitedPipelineApplicant(applicant)),
    [applicants],
  )
  const allSelectableSelected =
    selectableApplicants.length > 0 &&
    selectableApplicants.every((applicant) => selectedIds.has(applicant.id))
  const someSelectableSelected = selectableApplicants.some((applicant) => selectedIds.has(applicant.id))

  const unassigned = byStage.get(UNASSIGNED_PIPELINE_COLUMN_ID) ?? []
  const invitedStage = INVITED_PIPELINE_STAGE

  function renderApplicant(applicant: ApplicantCardModel, commitment?: 'applied' | 'invited') {
    const isInvited = isInvitedPipelineApplicant(applicant)
    const isSelected = selectedIds.has(applicant.id)

    return (
      <div key={applicant.id} className="flex items-start gap-2">
        {isInvited ? (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="mt-3 inline-flex shrink-0">
                  <Checkbox disabled checked={false} aria-label="Invited candidate cannot be selected" />
                </span>
              </TooltipTrigger>
              <TooltipContent>{INVITED_TOOLTIP}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          <Checkbox
            className="mt-3 shrink-0"
            checked={isSelected}
            onCheckedChange={() => onToggleSelect(applicant.id)}
            aria-label={`Select ${applicant.profile.name}`}
          />
        )}
        <div className="min-w-0 flex-1">
          <ApplicantCard
            applicant={applicant}
            stages={stages}
            density="rich"
            commitment={commitment}
            rank={rankByApplicationId.get(applicant.id)}
            scoresGate={scoresGate}
            analysisRetry={analysisRetry}
            onOpen={() => onOpenApplicant(applicant.id)}
            onMoveStage={(slug) => onMoveStage(applicant.id, slug)}
            onRecordDecision={(decision) => onRecordDecision(applicant.id, decision)}
            onOpenThread={onOpenThread ? () => onOpenThread(applicant.id) : undefined}
          />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {selectableApplicants.length > 0 ? (
        <div className="flex items-center gap-2 rounded-md border border-border/60 bg-muted/20 px-3 py-2">
          <Checkbox
            checked={allSelectableSelected ? true : someSelectableSelected ? 'indeterminate' : false}
            onCheckedChange={(checked) => {
              if (checked) onSelectAll()
              else onClearSelection()
            }}
            aria-label="Select all applicants"
          />
          <span className="text-sm text-muted-foreground">
            {selectedIds.size > 0
              ? `${selectedIds.size} selected`
              : `Select applicants (${selectableApplicants.length})`}
          </span>
        </div>
      ) : null}

      <section className="space-y-3">
        <div className="sticky top-0 z-[1] -mx-1 bg-background/95 py-1 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <StageColumnHeader
            stage={invitedStage}
            count={invited.length}
            subtitle="not yet applied"
            className="rounded-md border border-dashed border-border/60"
          />
        </div>
        {invited.length === 0 ? (
          <p className="px-0.5 text-sm text-muted-foreground">No one invited yet</p>
        ) : (
          <PaneResponsiveCardGrid className="gap-3">
            {invited.map((applicant) => renderApplicant(applicant, 'invited'))}
          </PaneResponsiveCardGrid>
        )}
      </section>
      {boardPipelineStages(stages).map((stage) => {
        const list = byStage.get(stage.slug) ?? []
        return (
          <section key={stage.slug} className="space-y-3">
            <div className="sticky top-0 z-[1] -mx-1 bg-background/95 py-1 backdrop-blur supports-[backdrop-filter]:bg-background/80">
              <StageColumnHeader stage={stage} count={list.length} className="rounded-md" />
            </div>
            {list.length === 0 ? (
              <p className="px-0.5 text-sm text-muted-foreground">No one at this stage yet</p>
            ) : (
              <PaneResponsiveCardGrid className="gap-3">
                {list.map((applicant) => renderApplicant(applicant))}
              </PaneResponsiveCardGrid>
            )}
          </section>
        )
      })}
      {unassigned.length > 0 ? (
        <section className="space-y-3">
          <div className="sticky top-0 z-[1] -mx-1 bg-background/95 py-1 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <StageColumnHeader stage={UNASSIGNED_PIPELINE_STAGE} count={unassigned.length} className="rounded-md" />
          </div>
          <p className="px-0.5 text-xs text-muted-foreground">
            These applicants are missing a pipeline stage. Move them to a column or refresh after the server assigns a stage.
          </p>
          <PaneResponsiveCardGrid className="gap-3">
            {unassigned.map((applicant) => renderApplicant(applicant))}
          </PaneResponsiveCardGrid>
        </section>
      ) : null}
    </div>
  )
}
